import React from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReadyBan from "../../assets/Readyban.png";
import { useTranslation } from "react-i18next";

// ─── Constants ───────────────────────────────────────────────────────────────
const BANNER_STYLES = {
  heights: { xs: "260px", sm: "300px", md: "340px" },
  borderRadius: "15px",
  overlayGradient:
    "linear-gradient(135deg, rgba(10,20,80,0.2) 0%, rgba(0,0,0,0.1) 100%)",
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function BannerImage({ src, alt, heights }) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      sx={{
        width: "100%",
        height: heights,
        objectFit: "cover",
        display: "block",
      }}
    />
  );
}

function BannerOverlay({ gradient }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        inset: 0,
        background: gradient,
      }}
    />
  );
}

function BannerContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const CTA_BUTTONS = [
    { label: t("categories.shop_now"), primary: true, path: "/category" },
    { label: t("navigation.contact_us"), primary: false, path: "/contact" },
  ];

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: { xs: 2, sm: 4 },
        gap: 1,
      }}
    >
      <Typography
        className="poppins"
        component="h2"
        sx={{
          fontSize: { xs: "1.25rem", sm: "1.85rem", md: "2.5rem" },
          fontWeight: 600,
          color: "#fff",
          lineHeight: 1.3,
        }}
      >
        {t("banner.ready_to_build")}
      </Typography>

      <Typography
        className="poppins max-w-3xl"
        sx={{
          color: "rgba(255,255,255,0.85)",
          fontSize: { xs: "0.82rem", sm: "0.97rem" },
          maxWidth: { xs: "100%", sm: "440px" },
          lineHeight: 1.6,
        }}
      >
        {t("banner.explore_pre")}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{
          mt: 1,
          alignItems: "center",
          gap: 2,
        }}
      >
        {CTA_BUTTONS.map((btn) => (
          <Button
            key={btn.label}
            variant={btn.primary ? "contained" : "outlined"}
            onClick={() => navigate(btn.path)}
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              textTransform: "none",
              px: 3,
              py: 0.9,
              minWidth: "140px",
              fontSize: "1rem",
              transition: "all 0.2s ease",
              backgroundColor: "#fff",
              color: "#000000",
              borderColor: "rgba(255,255,255,0.7)",
              "&:hover": {
                color: "white",
                backgroundColor: "rgba(2, 0, 0, 0.72)",
                borderColor: "#fff",
              },
            }}
          >
            {btn.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function ReadyBanner() {
  return (
    <Box
      component="section"
      aria-label="Call to action banner"
      sx={{
        px: { xs: 2, sm: 4, md: 6 },
        py: { xs: 4, sm: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: BANNER_STYLES.borderRadius,
          overflow: "hidden",
          width: "100%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        }}
      >
        <BannerImage
          src={ReadyBan}
          alt="Ready to build — premium construction materials across UAE"
          heights={BANNER_STYLES.heights}
        />
        <BannerOverlay gradient={BANNER_STYLES.overlayGradient} />
        <BannerContent />
      </Box>
    </Box>
  );
}

export default ReadyBanner;